import React, { useState, useEffect, useCallback } from 'react';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Refresh as ArrowPathIcon
} from '@mui/icons-material';
import {
  getPlaybookLibraryItems,
  createPlaybookLibraryItem,
  updatePlaybookLibraryItem,
  deletePlaybookLibraryItem,
  uploadPlaybookLibraryExcel,
  downloadPlaybookLibraryTemplate,
  type PlaybookLibraryItem,
  type PlaybookLibraryType
} from '../services/apiService';
import { Button } from './ui/Button';
import { useConfirm } from './ui/ConfirmDialog';

interface PlaybookLibraryManagementProps {
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface SectionConfig {
  type: PlaybookLibraryType;
  label: string;
}

const SECTIONS: SectionConfig[] = [
  { type: 'pitch', label: 'Intro Pitch' },
  { type: 'question', label: 'Qualifying Questions' },
  { type: 'advanced', label: 'Deal Progression' }
];

const PlaybookLibraryManagement: React.FC<PlaybookLibraryManagementProps> = ({
  setError,
  setSuccess
}) => {
  const confirm = useConfirm();
  const [items, setItems] = useState<PlaybookLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<PlaybookLibraryType>>(
    new Set(['pitch', 'question', 'advanced'])
  );

  // Inline add state per section
  const [addingText, setAddingText] = useState<Record<PlaybookLibraryType, string>>({
    pitch: '',
    question: '',
    advanced: ''
  });
  const [addingInSection, setAddingInSection] = useState<PlaybookLibraryType | null>(null);

  // Inline edit state
  const [deleting, setDeleting] = useState(false);

  // Inline edit state
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPlaybookLibraryItems();
      setItems(response?.items || []);
    } catch (error: any) {
      console.error('Error fetching library items:', error);
      setError('Failed to load library items');
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const getItemsByType = (type: PlaybookLibraryType): PlaybookLibraryItem[] => {
    return items.filter(item => item.type === type);
  };

  const toggleSection = (type: PlaybookLibraryType) => {
    const updated = new Set(expandedSections);
    if (updated.has(type)) {
      updated.delete(type);
    } else {
      updated.add(type);
    }
    setExpandedSections(updated);
  };

  const handleAddItem = async (type: PlaybookLibraryType) => {
    const text = addingText[type]?.trim();
    if (!text) return;

    try {
      await createPlaybookLibraryItem(type, text);
      setAddingText(prev => ({ ...prev, [type]: '' }));
      setAddingInSection(null);
      setSuccess('Item added');
      await fetchItems();
    } catch (error: any) {
      setError(error?.message || 'Failed to add item');
    }
  };

  const handleUpdateItem = async (id: string) => {
    const text = editingText.trim();
    if (!text) return;

    try {
      await updatePlaybookLibraryItem(id, text);
      setEditingItemId(null);
      setEditingText('');
      setSuccess('Item updated');
      await fetchItems();
    } catch (error: any) {
      setError(error?.message || 'Failed to update item');
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!(await confirm({
      title: 'Delete this item?',
      message: 'This cannot be undone.',
      confirmLabel: 'Delete item',
      variant: 'destructive',
    }))) return;

    setDeleting(true);
    try {
      await deletePlaybookLibraryItem(id);
      setSuccess('Item deleted');
      await fetchItems();
    } catch (error: any) {
      setError(error?.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleUploadExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadPlaybookLibraryExcel(file);
      setSuccess(result?.message || 'Upload complete');
      await fetchItems();
    } catch (error: any) {
      const errorMessage = error?.error && error.error !== 'API_ERROR'
        ? error.error
        : error?.message || 'Failed to upload file';
      setError(errorMessage);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadPlaybookLibraryTemplate();
    } catch (error: any) {
      setError(error?.message || 'Failed to download template');
    }
  };

  const startEditing = (item: PlaybookLibraryItem) => {
    setEditingItemId(item._id);
    setEditingText(item.text);
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setEditingText('');
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Deleting overlay */}
      {(deleting || uploading || (loading && items.length > 0)) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
          <div className="flex flex-col items-center gap-2">
            <ArrowPathIcon className="animate-spin text-signature" style={{ fontSize: '2.5rem' }} />
            <span className="text-sm font-medium text-ink-secondary">
              {deleting ? 'Deleting...' : uploading ? 'Uploading...' : 'Refreshing...'}
            </span>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="kb-playbook-library-guide px-4 py-3">
        <p className="text-sm text-ink-secondary">
          <span className="font-medium">Library:</span> Build a reusable corpus of pitch bullets, qualifying questions, and deal progression talking points. Upload via Excel or add individually. Select from this library when creating playbooks.
        </p>
      </div>

      {/* Top bar with upload/download */}
      <div className="px-4 py-2 border-b border-hairline flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-muted">
            {items.length} total item{items.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={fetchItems}
            variant="tertiary"
            size="sm"
            title="Refresh"
            disabled={loading}
            className="p-2"
          >
            <ArrowPathIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1.25rem' }} />
          </Button>
          <Button
            onClick={handleDownloadTemplate}
            variant="secondary"
            size="sm"
            className="flex items-center gap-2"
          >
            <DownloadIcon style={{ fontSize: '1rem' }} />
            Download Template
          </Button>

          <label
            className={`px-4 py-2 text-on-signature rounded-control flex items-center gap-2 text-sm cursor-pointer ${
              uploading ? 'bg-signature opacity-60 cursor-not-allowed' : 'bg-signature hover:bg-signature-strong'
            }`}
          >
            <UploadIcon style={{ fontSize: '1rem' }} />
            {uploading ? 'Uploading...' : 'Upload Excel'}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUploadExcel}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="animate-spin mx-auto text-ink-muted" style={{ fontSize: '2rem' }} />
            <p className="mt-2 text-ink-muted">Loading library...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {SECTIONS.map(({ type, label }) => {
              const sectionItems = getItemsByType(type);
              const isExpanded = expandedSections.has(type);

              return (
                <div key={type} className="kb-playbook-library-section bg-surface">
                  {/* Section header */}
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-surface-subtle"
                    onClick={() => toggleSection(type)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ExpandLessIcon className="text-ink-muted" />
                      ) : (
                        <ExpandMoreIcon className="text-ink-muted" />
                      )}
                      <h3 className="heading-4">
                        {label} ({sectionItems.length})
                      </h3>
                    </div>
                  </div>

                  {/* Section content */}
                  {isExpanded && (
                    <div className="border-t border-hairline px-4 py-3">
                      {sectionItems.length === 0 ? (
                        <p className="text-sm text-ink-muted italic mb-3">No items yet</p>
                      ) : (
                        <ul className="space-y-2 mb-3">
                          {sectionItems.map(item => (
                            <li key={item._id} className="flex items-center gap-2 group">
                              {editingItemId === item._id ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdateItem(item._id);
                                      if (e.key === 'Escape') cancelEditing();
                                    }}
                                    className="flex-1 px-3 py-1.5 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleUpdateItem(item._id)}
                                    className="p-1 text-success hover:bg-success-tint rounded"
                                    title="Save"
                                  >
                                    <CheckIcon style={{ fontSize: '1rem' }} />
                                  </button>
                                  <Button
                                    onClick={cancelEditing}
                                    variant="tertiary"
                                    size="sm"
                                    title="Cancel"
                                    className="p-1"
                                  >
                                    <CloseIcon style={{ fontSize: '1rem' }} />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <span className="text-ink-muted">-</span>
                                  <span className="flex-1 text-sm text-ink-secondary">{item.text}</span>
                                  <Button
                                    onClick={() => startEditing(item)}
                                    variant="tertiary"
                                    size="sm"
                                    title="Edit"
                                    className="p-1 text-ink-disabled hover:text-signature hover:bg-signature-tint opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <PencilIcon style={{ fontSize: '1.125rem' }} />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteItem(item._id)}
                                    variant="tertiary"
                                    size="sm"
                                    title="Delete"
                                    className="p-1 text-ink-disabled hover:text-error hover:bg-error-tint opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <TrashIcon style={{ fontSize: '1.125rem' }} />
                                  </Button>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Add item inline */}
                      {addingInSection === type ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={addingText[type]}
                            onChange={(e) => setAddingText(prev => ({ ...prev, [type]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddItem(type);
                              if (e.key === 'Escape') setAddingInSection(null);
                            }}
                            placeholder={`New ${label.toLowerCase()} item...`}
                            className="flex-1 px-3 py-1.5 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm"
                            autoFocus
                          />
                          <button
                            onClick={() => handleAddItem(type)}
                            className="kb-button kb-button-secondary px-3 py-1.5 text-sm"
                          >
                            Add
                          </button>
                          <Button
                            onClick={() => setAddingInSection(null)}
                            variant="secondary"
                            size="sm"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setAddingInSection(type)}
                          variant="tertiary"
                          size="sm"
                          className="text-signature hover:text-signature-strong hover:bg-transparent px-0 flex items-center gap-1"
                        >
                          <PlusIcon style={{ fontSize: '0.875rem' }} />
                          Add Item
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaybookLibraryManagement;
